import FormZeroAjax from '../../../../../../src/packages/form-zero/resources/js/FormZeroAjax.mjs';
import ArticleDeleteConfirmation from './ArticleDeleteConfirmation.js';

/**
 * Only the per-article revision and gallery-card UI live here.
 * Request transport, CSRF rotation, validation, and sequencing are FormZeroAjax.
 */
export default class ArticleImageGallery {
    constructor(translator, transport = null, toastNotifier = null, confirmationDialog = null) {
        this.translate = (text) => translator.trans(text);
        this.toastNotifier = toastNotifier;
        this.deleteConfirmation = confirmationDialog || new ArticleDeleteConfirmation(this.translate);
        this.confirming = new WeakSet();
        this.ajax = transport ?? new FormZeroAjax({
            translate: this.translate,
            notify: toastNotifier ? ({type, message}) => toastNotifier.show({type, message}) : null,
            statusClasses: {base: 'small ms-2', success: 'text-success', error: 'text-danger'},
        });
    }

    init(root = document) {
        const form = root.querySelector('#article-image-gallery-deletes[data-fz-token]');
        if (!form || form.dataset.galleryInitialized === 'true') return;
        form.dataset.galleryInitialized = 'true';
        form.addEventListener('click', (event) => {
            const trigger = event.target.closest?.('[data-fz-gallery-delete][data-fz-url]');
            if (!trigger || !form.contains(trigger)) return;
            event.preventDefault();
            void this.delete(form, trigger);
        });
    }

    async delete(form, trigger) {
        if (this.ajax.pending.has(trigger) || this.confirming.has(trigger)) return null;
        const card = trigger.closest('[data-article-id][data-editor-revision]');
        if (!card || !card.id || !/^[1-9]\d*$/.test(card.dataset.articleId) ||
            !/^[1-9]\d*$/.test(card.dataset.editorRevision)) return null;
        if (trigger.dataset.fzConfirm) {
            this.confirming.add(trigger);
            let approved = false;
            try { approved = await this.deleteConfirmation.ask(trigger.dataset.fzConfirm, trigger); }
            finally { this.confirming.delete(trigger); }
            if (!approved) return null;
        }

        this.ajax.pending.add(trigger);
        const priorDisabled = trigger.disabled;
        trigger.disabled = true;
        return this.ajax.enqueue(form, async () => {
            try {
                // Read the revision when the queued request starts. Another
                // deletion for the same article may already have advanced it.
                if (!card.isConnected) return null;
                const data = new FormData();
                data.set('editor_revision', card.dataset.editorRevision);
                const result = await this.ajax.request(form, trigger.dataset.fzUrl, data);
                if (!Number.isSafeInteger(result.editorRevision)) {
                    throw new Error('Invalid article revision in server response');
                }
                const articleId = card.dataset.articleId;
                card.remove();
                for (const other of form.querySelectorAll('[data-article-id][data-editor-revision]')) {
                    if (other.dataset.articleId === articleId) {
                        other.dataset.editorRevision = String(result.editorRevision);
                    }
                }
                if (result.warning) this.message(form, this.translate(result.warning), true);
                else this.message(form, this.translate('article.imageDeleted'));
                return result;
            } catch (error) {
                const message = error.conflict
                    ? this.translate('article.galleryConflict')
                    : this.translate(error.message || 'article.deleteFailed');
                if (this.toastNotifier) this.toastNotifier.error(message);
                else this.ajax.status(trigger, message, true);
                return null;
            } finally {
                this.ajax.pending.delete(trigger);
                trigger.disabled = priorDisabled;
            }
        });
    }

    message(form, text, warning = false) {
        if (this.toastNotifier) {
            if (warning) this.toastNotifier.warning(text);
            else this.toastNotifier.success(text);
            return;
        }
        let message = form.querySelector('[data-fz-gallery-status]');
        if (!message) {
            message = document.createElement('div');
            message.dataset.fzGalleryStatus = '';
            message.setAttribute('role', 'alert');
            form.prepend(message);
        }
        message.className = warning ? 'alert alert-warning' : 'alert alert-success';
        message.textContent = text;
    }
}
